import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../lib/LanguageContext';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Bell, CheckCircle, Clock, AlertTriangle, ClipboardList, Play, CheckCheck, X } from 'lucide-react';

const ICON_MAP = {
  'assignment_new': ClipboardList,
  'due_soon': Clock,
  'overdue': AlertTriangle,
  'completed': CheckCircle,
  'in_progress': Play,
};

const COLOR_MAP = {
  'assignment_new': 'text-[#003366] bg-blue-50',
  'due_soon': 'text-amber-600 bg-amber-50',
  'overdue': 'text-red-600 bg-red-50',
  'completed': 'text-emerald-600 bg-emerald-50',
  'in_progress': 'text-[#F37021] bg-orange-50',
};

function timeAgo(isoStr) {
  const now = new Date();
  const date = new Date(isoStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count);
    } catch {}
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggleOpen = async () => {
    const newOpen = !open;
    setOpen(newOpen);
    if (newOpen) {
      setLoading(true);
      try {
        const res = await api.get('/notifications');
        setNotifications(res.data);
        setUnreadCount(res.data.filter(n => !n.read).length);
      } catch {}
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      try {
        await api.put(`/notifications/${notif.id}/read`);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {}
    }
    setOpen(false);
    if (notif.link) navigate(notif.link);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleOpen}
        className="text-slate-500 hover:text-[#003366] relative"
        data-testid="notification-bell-btn"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-[#003366]" />
              <h3 className="font-semibold text-[#003366] text-sm" style={{fontFamily: 'Manrope, sans-serif'}}>{t('notif.title')}</h3>
              {unreadCount > 0 && (
                <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">{unreadCount}</Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" className="text-xs text-[#003366] hover:bg-blue-50 h-7 px-2" onClick={markAllRead} data-testid="mark-all-read-btn">
                  <CheckCheck size={14} className="mr-1" /> {t('notif.mark_all_read')}
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={() => setOpen(false)}>
                <X size={14} />
              </Button>
            </div>
          </div>

          <ScrollArea className="max-h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-3 border-[#003366] border-t-transparent rounded-full" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-10 px-4">
                <Bell className="mx-auto text-slate-200 mb-3" size={36} />
                <p className="text-slate-500 text-sm font-medium">{t('notif.no_notifications')}</p>
                <p className="text-slate-400 text-xs mt-1">{t('notif.you_are_caught_up')}</p>
              </div>
            ) : (
              <div>
                {notifications.map((notif, idx) => {
                  const Icon = ICON_MAP[notif.type] || Bell;
                  const colorClass = COLOR_MAP[notif.type] || 'text-slate-600 bg-slate-50';
                  return (
                    <div key={notif.id}>
                      <button
                        onClick={() => handleClick(notif)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex gap-3 ${!notif.read ? 'bg-blue-50/40' : ''}`}
                        data-testid={`notification-item-${notif.id}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <Icon size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm ${!notif.read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'} truncate`}>
                              {notif.title}
                            </p>
                            {!notif.read && (
                              <div className="w-2 h-2 bg-[#003366] rounded-full flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.message}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{timeAgo(notif.created_at)}</p>
                        </div>
                      </button>
                      {idx < notifications.length - 1 && <Separator />}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
